import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ParagraphReadingCreate = ({
  isModal = false,
  jobPostingId,
  screeningName = '',
  registerSubmit,
  onLoadingChange,
  onGenerationStart,
  onValidationChange,
  onGeneratedSections,
  onCreated,
  initialDraft,
  onDraftChange,
}) => {
  const [assessmentTitle, setAssessmentTitle] = useState(initialDraft?.title || '');
  const [paragraphs, setParagraphs] = useState(initialDraft?.paragraphs || [
    {
      id: 'p1',
      title: '',
      text: '',
      timeMinutes: 5,
      questions: [
        {
          id: 'q1',
          content: '',
          type: 'multiple-choice',
          options: [
            { text: '', isCorrect: true },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
          ],
          points: 1,
        },
      ],
    },
  ]);

  // Validation
  const isValid = () => {
    if (!assessmentTitle.trim()) return false;
    if (paragraphs.length === 0) return false;
    for (const p of paragraphs) {
      if (!p.title.trim()) return false;
      if (!p.text.trim() || p.text.length < 50) return false;
      if (!p.timeMinutes || p.timeMinutes < 1) return false;
      if (p.questions.length === 0) return false;
      for (const q of p.questions) {
        if (!q.content.trim()) return false;
        if (!q.points || q.points < 1) return false;
        
        if (q.type === 'multiple-choice') {
          if (q.options.length < 2) return false;
          let hasCorrect = false;
          for (const opt of q.options) {
            if (!opt.text.trim()) return false;
            if (opt.isCorrect) hasCorrect = true;
          }
          if (!hasCorrect) return false;
        } else if (q.type === 'true-false') {
          if (q.options.length !== 2) return false;
          if (!q.options.some(o => o.isCorrect)) return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    onValidationChange?.(isValid());
  }, [assessmentTitle, paragraphs, onValidationChange]);

  useEffect(() => {
    onDraftChange?.({ title: assessmentTitle, paragraphs });
  }, [assessmentTitle, paragraphs, onDraftChange]);

  useEffect(() => {
    if (typeof registerSubmit !== 'function') return;

    registerSubmit(async () => {
      if (!isValid()) {
        toast.error('Please fill all required fields. Make sure each question has content and its options are filled out.', { duration: 4000 });
        return false;
      }

      onLoadingChange?.(true);
      try {
        const questions = [];
        let totalDuration = 0;

        for (const paragraph of paragraphs) {
          totalDuration += paragraph.timeMinutes;
          for (const q of paragraph.questions) {
            questions.push({
              title: q.content.substring(0, 100),
              content: q.content,
              type: q.type === 'true-false' ? 'true-false' : 'multiple-choice',
              points: q.points,
              options: q.options.map(opt => ({ text: opt.text, isCorrect: opt.isCorrect })),
              correctAnswer: q.type === 'true-false'
                ? q.options.find(o => o.isCorrect)?.text || ''
                : '',
              passageMeta: {
                paragraphText: paragraph.text,
                paragraphTitle: paragraph.title,
                isParagraphReading: true,
              },
            });
          }
        }

        const formData = {
          title: assessmentTitle.trim(),
          duration: totalDuration,
          passingScore: 70,
          role: 'Communication',
          additionalContext: '',
          assessmentTypes: ['Communication'],
          questionTypes: ['Paragraph Reading'],
          category: 'communication',
          difficulty: 'medium',
        };

        // Instead of making an API call, we return the data
        // because the parent (AssessmentCreateFlowModal) expects us to return the assembled data
        // when registerSubmit is called from the parent's "Continue" button handler.
        // Wait, AssessmentCreate does API call and then calls onCreated. 
        // But for advanced flow, registerContinue is used which returns data.
        // Oh, wait! In AssessmentCreateFlowModal:
        // isParagraphReadingMode uses `registerSubmit={setDetailsSubmitSafe}`
        // And the parent handles it. Wait, `AssessmentCreate` returns `{ finish: true, formData, questions }`?
        // Let's check AssessmentCreate.js how it handles registerSubmit.
        
        return { finish: true, formData, questions };

      } catch (error) {
        toast.error('Failed to create paragraph reading assessment');
        return false;
      } finally {
        onLoadingChange?.(false);
      }
    });
  }, [registerSubmit, isValid, assessmentTitle, paragraphs, onLoadingChange]);

  const addParagraph = () => {
    if (paragraphs.length >= 3) {
      toast.error('Maximum 3 paragraphs allowed.');
      return;
    }
    setParagraphs([...paragraphs, {
      id: `p${Date.now()}`,
      title: '',
      text: '',
      timeMinutes: 5,
      questions: [
        {
          id: `q${Date.now()}`,
          content: '',
          type: 'multiple-choice',
          options: [
            { text: '', isCorrect: true },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
          ],
          points: 1,
        },
      ],
    }]);
  };

  const removeParagraph = (id) => {
    setParagraphs(paragraphs.filter(p => p.id !== id));
  };

  const updateParagraph = (id, field, value) => {
    setParagraphs(paragraphs.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const addQuestion = (paragraphId) => {
    setParagraphs(paragraphs.map(p => {
      if (p.id === paragraphId) {
        return {
          ...p,
          questions: [...p.questions, {
            id: `q${Date.now()}`,
            content: '',
            type: 'multiple-choice',
            options: [
              { text: '', isCorrect: true },
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
            ],
            points: 1,
          }]
        };
      }
      return p;
    }));
  };

  const removeQuestion = (paragraphId, questionId) => {
    setParagraphs(paragraphs.map(p => {
      if (p.id === paragraphId) {
        return { ...p, questions: p.questions.filter(q => q.id !== questionId) };
      }
      return p;
    }));
  };

  const updateQuestion = (paragraphId, questionId, field, value) => {
    setParagraphs(paragraphs.map(p => {
      if (p.id === paragraphId) {
        return {
          ...p,
          questions: p.questions.map(q => {
            if (q.id === questionId) {
              if (field === 'type') {
                if (value === 'true-false') {
                  return {
                    ...q,
                    type: value,
                    options: [
                      { text: 'True', isCorrect: true },
                      { text: 'False', isCorrect: false }
                    ]
                  };
                } else if (value === 'multiple-choice') {
                  return {
                    ...q,
                    type: value,
                    options: [
                      { text: '', isCorrect: true },
                      { text: '', isCorrect: false },
                      { text: '', isCorrect: false },
                      { text: '', isCorrect: false },
                    ]
                  };
                }
              }
              return { ...q, [field]: value };
            }
            return q;
          })
        };
      }
      return p;
    }));
  };

  const updateOption = (paragraphId, questionId, optionIndex, field, value) => {
    setParagraphs(paragraphs.map(p => {
      if (p.id === paragraphId) {
        return {
          ...p,
          questions: p.questions.map(q => {
            if (q.id === questionId) {
              const newOptions = [...q.options];
              if (field === 'isCorrect' && value === true) {
                // Ensure only one is correct
                newOptions.forEach(o => o.isCorrect = false);
              }
              newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
              return { ...q, options: newOptions };
            }
            return q;
          })
        };
      }
      return p;
    }));
  };

  const addOption = (paragraphId, questionId) => {
    setParagraphs(paragraphs.map(p => {
      if (p.id === paragraphId) {
        return {
          ...p,
          questions: p.questions.map(q => {
            if (q.id === questionId && q.options.length < 6) {
              return { ...q, options: [...q.options, { text: '', isCorrect: false }] };
            }
            return q;
          })
        };
      }
      return p;
    }));
  };

  const removeOption = (paragraphId, questionId, optionIndex) => {
    setParagraphs(paragraphs.map(p => {
      if (p.id === paragraphId) {
        return {
          ...p,
          questions: p.questions.map(q => {
            if (q.id === questionId && q.options.length > 2) {
              const newOptions = q.options.filter((_, idx) => idx !== optionIndex);
              // Ensure at least one is correct
              if (!newOptions.some(o => o.isCorrect)) {
                newOptions[0].isCorrect = true;
              }
              return { ...q, options: newOptions };
            }
            return q;
          })
        };
      }
      return p;
    }));
  };

  return (
    <div className={`mx-auto ${isModal ? 'w-full' : 'max-w-4xl'} space-y-6`}>
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Create Paragraph Reading Assessment</h3>
          <p className="text-xs text-gray-500 mt-1">Add up to 3 paragraphs with their associated questions.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assessment Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={assessmentTitle}
            onChange={(e) => setAssessmentTitle(e.target.value)}
            placeholder="e.g. Health & Wellness Comprehension"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF]"
          />
        </div>

        {paragraphs.map((p, pIndex) => (
          <div key={p.id} className="border border-gray-200 rounded-xl bg-gray-50 p-4 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">Paragraph {pIndex + 1}</h4>
              {paragraphs.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeParagraph(p.id)}
                  className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 font-medium"
                >
                  <TrashIcon className="w-3.5 h-3.5" /> Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Paragraph Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={p.title}
                  onChange={(e) => updateParagraph(p.id, 'title', e.target.value)}
                  placeholder="e.g. Healthy Eating"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Paragraph Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={p.text}
                  onChange={(e) => updateParagraph(p.id, 'text', e.target.value)}
                  placeholder="Enter the passage that candidates will read..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
                <p className="text-[10px] text-gray-500 mt-1">Minimum 50 characters.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Time Limit (minutes) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={p.timeMinutes}
                  onChange={(e) => updateParagraph(p.id, 'timeMinutes', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Total Points
                </label>
                <input
                  type="number"
                  disabled
                  value={p.questions.reduce((sum, q) => sum + (parseInt(q.points) || 0), 0)}
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-md text-sm text-gray-500"
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
              <h5 className="text-sm font-semibold text-gray-800">Questions</h5>
              
              {p.questions.map((q, qIndex) => (
                <div key={q.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-3">
                      <div className="flex gap-3">
                        <span className="text-sm font-medium text-gray-700 w-6">Q{qIndex + 1}.</span>
                        <input
                          type="text"
                          value={q.content}
                          onChange={(e) => updateQuestion(p.id, q.id, 'content', e.target.value)}
                          placeholder="Question text..."
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                        />
                        <select
                          value={q.type}
                          onChange={(e) => updateQuestion(p.id, q.id, 'type', e.target.value)}
                          className="w-40 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                        >
                          <option value="multiple-choice">MCQ (Single)</option>
                          <option value="true-false">True/False</option>
                        </select>
                      </div>

                      <div className="pl-9 space-y-2">
                        {q.type === 'multiple-choice' ? (
                          <>
                            {q.options.map((opt, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`correct-${p.id}-${q.id}`}
                                  checked={opt.isCorrect}
                                  onChange={() => updateOption(p.id, q.id, oIndex, 'isCorrect', true)}
                                  className="w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                                />
                                <input
                                  type="text"
                                  value={opt.text}
                                  onChange={(e) => updateOption(p.id, q.id, oIndex, 'text', e.target.value)}
                                  placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                                  className={`flex-1 px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] ${opt.isCorrect ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300'}`}
                                />
                                {opt.isCorrect && <span className="text-[10px] font-medium text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">Correct</span>}
                                {q.options.length > 2 && (
                                  <button
                                    type="button"
                                    onClick={() => removeOption(p.id, q.id, oIndex)}
                                    className="p-1.5 text-gray-400 hover:text-red-500"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                            {q.options.length < 6 && (
                              <button
                                type="button"
                                onClick={() => addOption(p.id, q.id)}
                                className="text-xs text-[#007BFF] hover:text-[#0062CC] font-medium mt-1"
                              >
                                + Add Option
                              </button>
                            )}
                          </>
                        ) : (
                          // True/False
                          <div className="space-y-2">
                            {q.options.map((opt, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`correct-${p.id}-${q.id}`}
                                  checked={opt.isCorrect}
                                  onChange={() => updateOption(p.id, q.id, oIndex, 'isCorrect', true)}
                                  className="w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                                />
                                <span className={`text-sm ${opt.isCorrect ? 'font-medium text-gray-900' : 'text-gray-600'}`}>{opt.text}</span>
                                {opt.isCorrect && <span className="text-[10px] font-medium text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded ml-2">Correct</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 pt-2">
                          <label className="text-xs font-medium text-gray-700">Points:</label>
                          <input
                            type="number"
                            min="0.1"
                            step="any"
                            value={q.points}
                            onChange={(e) => updateQuestion(p.id, q.id, 'points', parseFloat(e.target.value) || 1)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#007BFF]"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => removeQuestion(p.id, q.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded bg-gray-50 hover:bg-red-50"
                      title="Remove Question"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => addQuestion(p.id)}
                className="inline-flex items-center gap-1 text-sm text-[#007BFF] hover:text-[#0062CC] font-medium px-2 py-1"
              >
                <PlusIcon className="w-4 h-4" /> Add Question
              </button>
            </div>
          </div>
        ))}

        {paragraphs.length < 3 && (
          <button
            type="button"
            onClick={addParagraph}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:border-[#007BFF] hover:text-[#007BFF] hover:bg-[#007BFF]/5 transition-colors flex items-center justify-center gap-2"
          >
            <PlusIcon className="w-4 h-4" /> Add Paragraph
          </button>
        )}
      </div>
    </div>
  );
};

export default ParagraphReadingCreate;
